import type { CSSProperties } from 'react';
import type { GameSpec, PresentationSpec } from '../../../games/spec/schema.ts';
import type { Seat } from '../../../shared/domain.ts';

/**
 * The bridge from an authored `presentation` block to the DOM.
 *
 * Seat colours used to exist twice: once in `PresentationSpecSchema` and once as
 * `--seat-1` / `--seat-2` in `styles.css`. Two sources of truth for the same
 * fact, and the stylesheet was the one that actually rendered — so an authored
 * theme was invisible no matter what the author chose.
 *
 * These helpers make the spec authoritative by emitting custom properties that
 * *override* the stylesheet's values on the board's own subtree. The CSS keeps
 * its declarations, which now serve their proper purpose: the fallback for a
 * seat a spec does not describe, and the colours the rest of the page uses.
 */

/** The custom properties a spec's theme sets, for the board's root element. */
export function themeVariables(spec: GameSpec): CSSProperties {
  const { theme } = spec.presentation;
  const style: Record<string, string> = {
    '--spec-canvas': theme.canvas,
    '--spec-board': theme.board,
  };

  for (const [seat, appearance] of Object.entries(theme.seats)) {
    style[`--seat-${seat}`] = appearance.color;
    // The non-colour channel, so a themed board keeps the marker distinction
    // that makes it readable without hue (WCAG 1.4.1).
    style[`--seat-${seat}-marker`] = appearance.marker;
  }

  return style as CSSProperties;
}

/** The marker shape a seat uses, defaulting to the accessible alternation. */
export function markerOf(spec: GameSpec, seat: Seat): 'filled' | 'ring' {
  return spec.presentation.theme.seats[String(seat)]?.marker ?? 'filled';
}

/**
 * Fills a `cellTemplate` such as `'row {row}, column {col}, {occupant}'`.
 *
 * Rows and columns are 1-based here because this text is read aloud, and "row
 * 0" is not how anyone describes a board out loud.
 */
export function cellLabel(
  labels: PresentationSpec['labels'],
  row: number,
  col: number,
  occupant: string,
): string {
  return labels.cellTemplate
    .replaceAll('{row}', String(row + 1))
    .replaceAll('{col}', String(col + 1))
    .replaceAll('{occupant}', occupant);
}

/**
 * How to describe who is in a cell, in the authored vocabulary.
 *
 * The spec's `own`/`other` pair only makes sense to somebody who has a seat.
 * A spectator has none, and calling every occupied cell "other" would make the
 * two players indistinguishable to a screen reader while remaining perfectly
 * distinct on screen — so the seatless case names the seat instead.
 */
export function occupantLabel(
  labels: PresentationSpec['labels'],
  cell: number,
  yourSeat: Seat | null,
): string {
  if (cell === 0) return labels.occupant.empty;
  if (yourSeat === null) return `player ${cell}`;
  return cell === yourSeat ? labels.occupant.own : labels.occupant.other;
}

/**
 * The order to render rows in, top of the screen first.
 *
 * `renderRowZeroAt: 'bottom'` is gravity: row 0 is the floor, so it is drawn
 * last. `'top'` is a plain reading order for a game where index 0 is the
 * top-left cell.
 */
export function rowRenderOrder(spec: GameSpec): number[] {
  const { rows } = spec.rules.board;
  const indices = Array.from({ length: rows }, (_, row) => row);
  return spec.presentation.layout.renderRowZeroAt === 'bottom' ? indices.reverse() : indices;
}
